import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Phone, MapPin, Facebook, Instagram, Twitter, Youtube, Shield, Truck, CreditCard, Wallet } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const navigate = useNavigate();
  const [footerSettings, setFooterSettings] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    fetchFooterSettings();
    fetchSiteSettings();
  }, []);

  const fetchFooterSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cms/footer`);
      setFooterSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch footer settings:', error);
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cms/site-settings`);
      setSiteSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch site settings:', error);
    }
  };

  const defaultColumns = [
    {
      title: 'Quick Links',
      links: [
        { label: 'Home', path: '/' },
        { label: 'Shop', path: '/shop' },
        { label: 'About Us', path: '/about' },
        { label: 'Contact', path: '/contact' },
        { label: 'Track Order', path: '/track-order' },
      ]
    },
    {
      title: 'Categories',
      links: [
        { label: 'Idols & Murtis', path: '/shop?category=idols-murtis' },
        { label: 'Puja Thali Sets', path: '/shop?category=puja-thali-sets' },
        { label: 'Diyas & Lamps', path: '/shop?category=diyas-lamps' },
        { label: 'Agarbatti', path: '/shop?category=agarbatti-dhoop' },
        { label: 'Rudraksha', path: '/shop?category=rudraksha' },
      ]
    },
    {
      title: 'Policies',
      links: [
        { label: 'Privacy Policy', path: '/privacy-policy' },
        { label: 'Shipping Policy', path: '/shipping-policy' },
        { label: 'Refund Policy', path: '/refund-policy' },
        { label: 'Terms of Service', path: '/terms' },
        { label: 'FAQ', path: '/faq' },
      ]
    },
  ];

  const columns = footerSettings?.columns?.length > 0 ? footerSettings.columns : defaultColumns;

  return (
    <footer className="bg-[#1A0505] text-white mt-auto" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center">
                <span className="text-maroon font-accent text-2xl">ॐ</span>
              </div>
              <div>
                <h3 className="text-2xl font-heading font-bold text-gold">
                  {siteSettings?.site_name || 'Indicraft'}
                </h3>
                <span className="text-xs text-gold/60 font-accent tracking-wider">
                  SACRED PUJA ESSENTIALS
                </span>
              </div>
            </div>
            
            <p className="text-sm text-gray-300 leading-relaxed mb-6">
              {footerSettings?.about_text || 'Indicraft brings you authentic puja essentials crafted with devotion. We are committed to providing sacred products that enhance your spiritual journey.'}
            </p>

            <div className="space-y-3">
              {siteSettings?.contact_email && (
                <a 
                  href={`mailto:${siteSettings.contact_email}`}
                  className="flex items-center gap-3 text-sm text-gray-300 hover:text-gold transition-colors"
                >
                  <Mail className="w-4 h-4 text-gold" />
                  {siteSettings.contact_email}
                </a>
              )}
              {siteSettings?.contact_phone && (
                <a 
                  href={`tel:${siteSettings.contact_phone}`}
                  className="flex items-center gap-3 text-sm text-gray-300 hover:text-gold transition-colors"
                >
                  <Phone className="w-4 h-4 text-gold" />
                  {siteSettings.contact_phone}
                </a>
              )}
              {siteSettings?.address && (
                <div className="flex items-start gap-3 text-sm text-gray-300">
                  <MapPin className="w-4 h-4 text-gold mt-0.5" />
                  {siteSettings.address}
                </div>
              )}
            </div>
          </div>

          {columns.map((column, index) => (
            <div key={index}>
              <h4 className="text-lg font-heading font-semibold text-gold mb-4 border-b border-gold/20 pb-2">
                {column.title}
              </h4>
              <ul className="space-y-2">
                {column.links?.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <button
                      onClick={() => navigate(link.path)}
                      className="text-sm text-gray-300 hover:text-gold hover:pl-2 transition-all duration-300"
                      data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-wrap justify-center gap-8 mb-8">
            <div className="flex items-center gap-2 text-sm text-gray-300">
              <Shield className="w-5 h-5 text-gold" />
              <span>100% Authentic</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-300">
              <Truck className="w-5 h-5 text-gold" />
              <span>Free Shipping</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-300">
              <Wallet className="w-5 h-5 text-gold" />
              <span>COD Available</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-300">
              <CreditCard className="w-5 h-5 text-gold" />
              <span>Secure Payment</span>
            </div>
          </div>

          <div className="flex justify-center gap-4 mb-8">
            {siteSettings?.facebook_url && (
              <a
                href={siteSettings.facebook_url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-gold/20 flex items-center justify-center transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5 text-gold" />
              </a>
            )}
            {siteSettings?.instagram_url && (
              <a
                href={siteSettings.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-gold/20 flex items-center justify-center transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5 text-gold" />
              </a>
            )}
            {siteSettings?.twitter_url && (
              <a
                href={siteSettings.twitter_url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-gold/20 flex items-center justify-center transition-colors"
                data-testid="social-twitter"
              >
                <Twitter className="w-5 h-5 text-gold" />
              </a>
            )}
            {siteSettings?.youtube_url && (
              <a
                href={siteSettings.youtube_url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-gold/20 flex items-center justify-center transition-colors"
                data-testid="social-youtube"
              >
                <Youtube className="w-5 h-5 text-gold" />
              </a>
            )}
            {!siteSettings?.facebook_url && !siteSettings?.instagram_url && !siteSettings?.twitter_url && (
              <>
                <a
                  href="#"
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-gold/20 flex items-center justify-center transition-colors"
                >
                  <Facebook className="w-5 h-5 text-gold" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-gold/20 flex items-center justify-center transition-colors"
                >
                  <Instagram className="w-5 h-5 text-gold" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-gold/20 flex items-center justify-center transition-colors"
                >
                  <Twitter className="w-5 h-5 text-gold" />
                </a>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 bg-black/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-sm text-gray-400">
            {footerSettings?.copyright_text || '© 2026 Indicraft. All Rights Reserved. Made with devotion in India.'}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
